import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { ACCESS, ACCESS_LABEL, wouldCreateCycle, type User } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { AddEmployeeModal } from '@/components/admin/AddEmployeeModal';
import {
  BulkUploadModal,
  ORG_TEMPLATE_HEADERS,
  orgTemplateRows,
} from '@/components/admin/BulkUploadModal';
import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { DeactivateModal } from '@/components/admin/DeactivateModal';
import { EmployeeEditModal } from '@/components/admin/EmployeeEditModal';
import { OrgTree } from '@/components/admin/OrgTree';
import { downloadWorkbook, todayStamp, type CellValue } from '@/components/admin/sheets';
import {
  departmentsOf,
  formatDateString,
  managerName,
  splitName,
} from '@/components/admin/helpers';
import { useAuth } from '@/auth/AuthContext';
import { useDirectory } from '@/components/admin/useDirectory';
import { DataTable, type Column } from '@/components/DataTable';
import { EmptyState } from '@/components/EmptyState';
import { LoadingBlock } from '@/components/Spinner';
import { useToast } from '@/components/Toast';

export function OrgChartPage(): ReactNode {
  const { users, directory, loading, error, reload } = useDirectory();
  const { refreshIfSelf } = useAuth();
  const { toast } = useToast();

  const [dept, setDept] = useState('');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set<string>());
  const [showInactive, setShowInactive] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState<{ employee: User; manager: User } | null>(null);

  const departments = useMemo(() => departmentsOf(users), [users]);
  const active = useMemo(() => users.filter((u) => u.active), [users]);
  const inactive = useMemo(() => users.filter((u) => !u.active), [users]);

  // Modals hold an id rather than a snapshot so a reload cannot leave them
  // editing a stale copy of the record.
  const editing = editingId === null ? null : (users.find((u) => u.id === editingId) ?? null);
  const deactivating =
    deactivatingId === null ? null : (users.find((u) => u.id === deactivatingId) ?? null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return active.filter((u) => {
      if (dept && u.department !== dept) return false;
      if (!q) return true;
      return (
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.position.toLowerCase().includes(q)
      );
    });
  }, [active, dept, search]);

  const toggleNode = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const requestReparent = useCallback(
    (employeeId: string, managerId: string) => {
      const employee = users.find((u) => u.id === employeeId);
      const manager = users.find((u) => u.id === managerId);
      if (!employee || !manager) return;
      // The server refuses a loop too; checking here avoids a pointless round
      // trip and an error the admin can prevent.
      if (wouldCreateCycle(directory, employeeId, managerId)) {
        toast(
          `${manager.name} already reports to ${employee.name} — that change would create a reporting loop.`,
          'err',
        );
        return;
      }
      setPendingMove({ employee, manager });
    },
    [directory, toast, users],
  );

  const applyReparent = useCallback(async (): Promise<void> => {
    if (!pendingMove) return;
    await api.admin.setReportsTo<User>({
      userId: pendingMove.employee.id,
      managerId: pendingMove.manager.id,
    });
    await reload();
    // Your own manager decides your approval chain, so a claim started right
    // after this must route by the new line, not the one cached at sign-in.
    await refreshIfSelf(pendingMove.employee.id);
    toast(`${pendingMove.employee.name} now reports to ${pendingMove.manager.name}.`, 'ok');
    setPendingMove(null);
  }, [pendingMove, reload, refreshIfSelf, toast]);

  const reactivate = useCallback(
    async (target: User): Promise<void> => {
      try {
        await api.admin.reactivateUser<User>({ userId: target.id });
        await reload();
        toast(`${target.name} reactivated.`, 'ok');
      } catch (err) {
        toast(errorMessage(err), 'err');
      }
    },
    [reload, toast],
  );

  const exportOrganization = useCallback((): void => {
    const rows: CellValue[][] = [[...ORG_TEMPLATE_HEADERS]];
    for (const u of active) {
      const { first, last } = splitName(u.name);
      const manager = u.reportsTo ? users.find((m) => m.id === u.reportsTo) : undefined;
      rows.push([
        u.empId,
        first,
        last,
        u.email,
        u.department,
        u.position,
        u.employmentLevel,
        manager?.email ?? '',
      ]);
    }
    void downloadWorkbook(`GDEC_Organization_${todayStamp()}.xlsx`, [{ name: 'Organization', rows }]);
    toast(`Exported ${active.length} active employee(s).`, 'ok');
  }, [active, toast, users]);

  const directoryColumns: ReadonlyArray<Column<User>> = [
    {
      key: 'name',
      header: 'Name',
      cell: (u) => (
        <>
          <span className="link-name">{u.name}</span>
          <br />
          <span className="sub-line">{u.email}</span>
        </>
      ),
    },
    { key: 'department', header: 'Department', cell: (u) => u.department },
    { key: 'position', header: 'Position', cell: (u) => u.position },
    { key: 'level', header: 'Employment Level', cell: (u) => u.employmentLevel },
    { key: 'reportsTo', header: 'Reports To', cell: (u) => managerName(users, u.reportsTo) },
    {
      key: 'access',
      header: 'System Access',
      cell: (u) => <span className="tag-chip">{ACCESS_LABEL[u.systemAccess]}</span>,
    },
    {
      key: 'actions',
      header: '',
      cell: (u) => (
        <button
          type="button"
          className="btn btn-danger btn-sm"
          disabled={u.systemAccess === ACCESS.MAIN_ACCESS}
          title={
            u.systemAccess === ACCESS.MAIN_ACCESS
              ? 'Transfer Main Access before deactivating this account.'
              : undefined
          }
          onClick={(e) => {
            e.stopPropagation();
            setDeactivatingId(u.id);
          }}
        >
          Deactivate
        </button>
      ),
    },
  ];

  const inactiveColumns: ReadonlyArray<Column<User>> = [
    {
      key: 'name',
      header: 'Name',
      cell: (u) => (
        <>
          <b>{u.name}</b>
          <br />
          <span className="sub-line">{u.email}</span>
        </>
      ),
    },
    { key: 'department', header: 'Department', cell: (u) => u.department },
    { key: 'position', header: 'Position', cell: (u) => u.position },
    {
      key: 'separation',
      header: 'Separation Date',
      cell: (u) => formatDateString(u.separationDate),
    },
    { key: 'reason', header: 'Reason', cell: (u) => u.deactivationReason || '—' },
    {
      key: 'actions',
      header: '',
      cell: (u) => (
        <button type="button" className="btn btn-good btn-sm" onClick={() => void reactivate(u)}>
          Reactivate
        </button>
      ),
    },
  ];

  if (loading && users.length === 0) return <LoadingBlock label="Loading the organization…" />;

  return (
    <>
      {error ? <div className="auth-error">{error}</div> : null}

      <div className="panel">
        <h3>
          Organization Chart &amp; Approval Hierarchy
          <div className="actions">
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
              ＋ Add Employee
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() =>
                void downloadWorkbook('GDEC_Organization_Template.xlsx', [
                  { name: 'Template', rows: orgTemplateRows() },
                ])
              }
            >
              Download Template
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={exportOrganization}>
              Export Current Organization
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setBulkOpen(true)}
            >
              Bulk Upload
            </button>
          </div>
        </h3>
        <p className="panel-note">
          Select an employee — in the tree or the directory below — to edit their details, system
          access or reporting line. A card can also be dragged onto a new manager; the same change
          is available from the &quot;Reports To&quot; field in the edit panel.
        </p>

        <div className="filter-bar">
          <label className="sub-line" htmlFor="org-dept">
            Department
          </label>
          <select id="org-dept" value={dept} onChange={(e) => setDept(e.target.value)}>
            <option value="">All Departments ({departments.length})</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d} ({active.filter((u) => u.department === d).length})
              </option>
            ))}
          </select>
          <input
            className="grow"
            aria-label="Search employees"
            placeholder="Search employee name, email, position…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setDept('');
              setSearch('');
            }}
          >
            Clear Filter
          </button>
        </div>

        {!dept && !search ? (
          <div className="dept-tiles">
            {departments.map((d) => {
              const members = active.filter((u) => u.department === d);
              return (
                <button
                  key={d}
                  type="button"
                  className="tree-card tile"
                  onClick={() => setDept(d)}
                >
                  <span className="t">{d}</span>
                  <span className="s">
                    {members.length} employee{members.length === 1 ? '' : 's'}
                  </span>
                </button>
              );
            })}
            {departments.length === 0 ? <EmptyState title="No active employees yet." /> : null}
          </div>
        ) : (
          <OrgTree
            users={users}
            directory={directory}
            scopeDept={dept}
            search={search.trim()}
            collapsed={collapsed}
            onToggle={toggleNode}
            onEdit={(u) => setEditingId(u.id)}
            onReparent={requestReparent}
          />
        )}
      </div>

      <div className="panel">
        <h3>
          Employee Directory <span className="tag-chip">{active.length} active</span>
          <div className="actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setShowInactive((v) => !v)}
            >
              {showInactive ? 'Hide' : 'View'} Inactive Employees
            </button>
          </div>
        </h3>
        <DataTable
          rows={filtered}
          columns={directoryColumns}
          rowKey={(u) => u.id}
          rowClassName={() => 'emp-row'}
          onRowClick={(u) => setEditingId(u.id)}
          empty={<EmptyState title="No employees match this filter." />}
        />
      </div>

      {showInactive ? (
        <div className="panel">
          <h3>
            Inactive Employees <span className="tag-chip">{inactive.length}</span>
          </h3>
          <DataTable
            rows={inactive}
            columns={inactiveColumns}
            rowKey={(u) => u.id}
            empty={<EmptyState title="No inactive employees." />}
          />
        </div>
      ) : null}

      {editing ? (
        <EmployeeEditModal
          target={editing}
          users={users}
          directory={directory}
          onClose={() => setEditingId(null)}
          onChanged={reload}
        />
      ) : null}

      {deactivating ? (
        <DeactivateModal
          target={deactivating}
          directory={directory}
          onClose={() => setDeactivatingId(null)}
          onChanged={reload}
        />
      ) : null}

      {adding ? (
        <AddEmployeeModal users={users} onClose={() => setAdding(false)} onChanged={reload} />
      ) : null}

      {bulkOpen ? (
        <BulkUploadModal onClose={() => setBulkOpen(false)} onChanged={reload} />
      ) : null}

      {pendingMove ? (
        <ConfirmDialog
          title="Change Reporting Relationship?"
          confirmLabel="Confirm Change"
          onCancel={() => setPendingMove(null)}
          onConfirm={applyReparent}
        >
          <p>
            Employee: <b>{pendingMove.employee.name}</b>
          </p>
          <p>
            Current manager: <b>{managerName(users, pendingMove.employee.reportsTo)}</b>
          </p>
          <p>
            New manager: <b>{pendingMove.manager.name}</b>
          </p>
          <p className="panel-note">
            This changes future approval routing for {pendingMove.employee.name}. Claims already in
            progress keep the approvers they were routed to.
          </p>
        </ConfirmDialog>
      ) : null}
    </>
  );
}
